import React, { SyntheticEvent, useState, useEffect } from 'react';
import axios from 'axios';
import './index.css';
import PropTypes from 'prop-types';
import swal from 'sweetalert';
import termsConditions from './termsCondition';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { vetsAreas } from '../../const/vetsAreas';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useDispatch } from 'react-redux';
import useSignOut from '../SignOutForm/instantSignOut';
import UploadButton from '../Common/UploadButton';
import { UploadedImages } from '../../Types/VetTypes';

type RegisterVetFormProps = {
  id: string,
  role: number,
  toggleServiceRegister: React.MouseEventHandler,
  closeServiceOpenRegister: React.MouseEventHandler,
  closeServiceOpenLogIn: React.MouseEventHandler,
  toggleSignUpUser: React.MouseEventHandler
};

type RegisterData = {
  termsConditionsAccepted: boolean,
  area: string,
};

function RegisterVetForm(props: RegisterVetFormProps) {
  const dispatch = useDispatch();
  const [datos, setDatos] = useState<RegisterData>({
    termsConditionsAccepted: false,
    area: ''
  });
  const [agree, setAgree] = useState<boolean>(false);
  const [showTermsConditions, setShowTermsConditions] = useState<boolean>(false);
  const [showRegisterSucceed, setShowRegisterSucceed] = useState<boolean>(false);
  const [colmevetNumber, setColmevetNumber] = useState<number>();
  const [isFormValid, setIsFormValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState<{
    titlePhotoUrl: string,
    carnetPhotoUrl: string
  }>({ titlePhotoUrl: '', carnetPhotoUrl: '' });

  useEffect(() => {
    setDatos(prev => ({ ...prev }));
  }, []);

  const signOut = useSignOut(dispatch);

  const handleDialogClose = (event: any) => {
    signOut(event);
    setShowRegisterSucceed(false);
  };

  function handleInputChange(e: SyntheticEvent) {
    const target = e.target as HTMLInputElement;
    setDatos({
      ...datos,
      [target.name]: target.value
    });
  }

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (!isFormValid) {
      alert('Por favor, asegúrate de subir ambas imágenes, seleccionar un área y aceptar los términos.');
      return;
    }

    setLoading(true);
    datos.termsConditionsAccepted = agree;
    const token = JSON.parse(localStorage.getItem('token') || '');

    const handleError = (error: any) => {
      setLoading(false);
      swal({
        title: 'Error',
        text: String(error.response?.data?.message || 'Ocurrió un error'),
        icon: 'error',
        buttons: { Aceptar: true },
        timer: 2000
      });
    };

    const payload = {
      vetId: props.id,
      colmevetNumber,
      titlePhotoUrl: photos.titlePhotoUrl,
      carnetPhotoUrl: photos.carnetPhotoUrl
    };

    const postVerification = () =>
      axios.post(`${process.env.REACT_APP_URL}/vets/verificationRequests`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

    if (props.role === 1) {
      axios.post(`${process.env.REACT_APP_URL}/create/vet`, datos, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(postVerification)
        .then(() => {
          setLoading(false);
          setShowRegisterSucceed(true);
        })
        .catch(handleError);
    } else {
      postVerification()
        .then(() => {
          setLoading(false);
          setShowRegisterSucceed(true);
        })
        .catch(handleError);
    }
  };

  const handleFileUpload = (type: 'titlePhotoUrl' | 'carnetPhotoUrl') => (base64: string) => {
    setPhotos(prevPhotos => {
      const newPhotos = { ...prevPhotos, [type]: base64 };
      const isValid = ImagesAreUploaded(newPhotos) && datos.area !== '' && agree;

      if (!isValid) {
        alert('Por favor, asegúrate de subir ambas imágenes, seleccionar un área y aceptar los términos.');
      }

      setIsFormValid(isValid);
      return newPhotos;
    });
  };

  const ImagesAreUploaded = (uploadedImages: UploadedImages): boolean =>
    !!uploadedImages.titlePhotoUrl && !!uploadedImages.carnetPhotoUrl;

  return (
    <form className="vetForm" onSubmit={handleSubmit}>
      <ul className="nav nav-pills justify-content-center nav-fill">
        <button className='closeForm' onClick={props.toggleSignUpUser}><CloseIcon /></button>
      </ul>
      <div className='register-title-div'>
        <h1 className='register-title'>¡Regístrate como médico/a veterinarios/a!</h1>
      </div>
      <div className='register-subtitle-div'>
        <p>No olvides leer los términos y condiciones</p>
      </div>
      <div className='vet-form-container'>
        <fieldset className='fieldset-div'>
          <p>
            <input
              id="colmevetNumber"
              placeholder=" "
              type="number"
              value={colmevetNumber || ''}
              name="colmevetNumber"
              onChange={e => setColmevetNumber(parseInt(e.target.value))}
              className="colmevet-number"
            />
            <label htmlFor="colmevetNumber">Número Colmevet (opcional)</label>
          </p>
        </fieldset>
        <fieldset className='fieldset-div'>
          <p>
            <select
              id="area"
              name="area"
              className="register-selector form-select"
              value={datos.area}
              onChange={handleInputChange}
              required
            >
              <option value="" disabled hidden></option>
              {vetsAreas.map(area => (
                <option key={area} value={area}>{area}</option>
              ))}
            </select>
            <label htmlFor="area">Área de desempeño</label>
          </p>
        </fieldset>
        <fieldset className='fieldset-titulo'>
          <UploadButton
            onFileUpload={handleFileUpload('titlePhotoUrl')}
            labelName='Título Profesional de Med. Veterinaria'
          />
        </fieldset>
        <fieldset className='fieldset-ci'>
          <UploadButton
            onFileUpload={handleFileUpload('carnetPhotoUrl')}
            labelName='Cédula de Identidad chilena'
          />
        </fieldset>
        <div className='terms-and-conditions'>
          <input
            className='terms-check'
            type="checkbox"
            id="agree"
            name='termsConditionsAccepted'
            checked={agree}
            onChange={() => {
              const nextAgree = !agree;
              setAgree(nextAgree);
              setIsFormValid(ImagesAreUploaded(photos) && datos.area !== '' && nextAgree);
            }}
            required
          />
          <div className='terms-label'>
            <div className='accept-terms'>Acepto los términos, condiciones y las políticas de privacidad</div>
            <a onClick={() => setShowTermsConditions(true)} className='show-terms'> Mostrar términos y condiciones </a>
          </div>
          <Dialog open={showTermsConditions} scroll='paper' maxWidth='lg'>
            <DialogActions>
              <button className='closeForm' onClick={() => setShowTermsConditions(false)}><CloseIcon /></button>
            </DialogActions>
            <DialogTitle id="scroll-dialog-title">
              <h4><strong>TÉRMINOS Y CONDICIONES Y AVISO DE PRIVACIDAD</strong></h4>
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="scroll-dialog-description" tabIndex={-1}>
                {termsConditions()}
              </DialogContentText>
            </DialogContent>
          </Dialog>
          <Dialog open={showRegisterSucceed} scroll='paper' maxWidth='lg' className="custom-dialog">
            <DialogActions>
              <button className='closeForm' onClick={handleDialogClose}><CloseIcon /></button>
            </DialogActions>
            <DialogTitle className="dialog-title" id="scroll-dialog-title">
              <h4><CheckCircleIcon className="success-icon" /> <strong>Has enviado tu solicitud con éxito!</strong></h4>
            </DialogTitle>
            <DialogContent>
              <DialogContentText tabIndex={-1} className="dialog-content-text">
                <div className='spacer32'></div>
                <p className='pop-up-text'> Para ver los cambios en tu cuenta, debes esperar a que el administrador te acepte y luego iniciar sesión </p>
              </DialogContentText>
            </DialogContent>
          </Dialog>
        </div>
        <div className='submit-button-container'>
          <button
            id='create-account'
            className='register-form-button'
            value='submit'
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} style={{ color: 'white' }} /> : 'Registrar'}
          </button>
        </div>
      </div>
    </form>
  );
}

RegisterVetForm.propTypes = {
  toggleServiceRegister: PropTypes.func,
  closeServiceOpenRegister: PropTypes.func,
  closeServiceOpenLogIn: PropTypes.func
};

export default RegisterVetForm;
